import type { RichTableBlockModel } from '@blocksuite/affine-model';
import { ShadowlessElement } from '@blocksuite/std';
import { html } from 'lit';
import { property } from 'lit/decorators.js';

import type { RichTableCellBlockComponent } from './rich-table-cell.js';
import {
  MIN_COLUMN_WIDTH,
  richTableResizerLineStyles,
  richTableResizerStyles,
} from './styles.js';

type DragState = {
  pointerId: number;
  startX: number;
  table: RichTableBlockModel;
  grid: HTMLElement;
  index: number;
  widths: number[];
  startWidth: number;
};

/**
 * Grab strip on a cell's right edge. Dragging it resizes that one column and
 * nothing else — the table gets wider or narrower to match.
 *
 * The width is written to the model once, on pointer-up, so a resize is a
 * single undo step; the drag itself only restyles the grid.
 */
export class RichTableResizerComponent extends ShadowlessElement {
  private _drag: DragState | null = null;

  @property({ attribute: false })
  accessor cell!: RichTableCellBlockComponent;

  private readonly _onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;

    const state = this._captureDragState(event);
    if (!state) return;

    // Keeps the drag from starting a text selection or moving the caret.
    event.preventDefault();
    event.stopPropagation();

    this._drag = state;
    this.dataset.dragging = 'true';
    this.setPointerCapture(event.pointerId);
  };

  private readonly _onPointerMove = (event: PointerEvent) => {
    const drag = this._drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    drag.grid.style.gridTemplateColumns = this._widthsAt(drag, event.clientX)
      .map(width => `${width}px`)
      .join(' ');
  };

  private readonly _onPointerUp = (event: PointerEvent) => {
    const drag = this._drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this._drag = null;
    delete this.dataset.dragging;
    this.releasePointerCapture(event.pointerId);

    const store = drag.table.store;
    if (store.readonly) return;

    const widths = this._widthsAt(drag, event.clientX);
    store.transact(() => {
      drag.table.props.columnWidths = widths;
    });
  };

  private _captureDragState(event: PointerEvent): DragState | null {
    const table = this.cell?.table;
    const index = this.cell?.columnIndex ?? -1;
    if (!table || index < 0) return null;
    if (table.store.readonly) return null;

    const widths = [...table.props.columnWidths];
    if (index >= widths.length) return null;

    const grid = this.cell.closest<HTMLElement>('.affine-rich-table-grid');
    if (!grid) return null;

    return {
      pointerId: event.pointerId,
      startX: event.clientX,
      table,
      grid,
      index,
      widths,
      // Measured rather than read from the model, so a drag still starts from
      // the right place if the two have drifted.
      startWidth: this.cell.getBoundingClientRect().width,
    };
  }

  /** Maps a pointer x onto the dragged column's new width. */
  private _widthsAt(drag: DragState, clientX: number) {
    const widths = [...drag.widths];
    widths[drag.index] = Math.max(
      MIN_COLUMN_WIDTH,
      Math.round(drag.startWidth + (clientX - drag.startX))
    );
    return widths;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add('affine-rich-table-resizer', richTableResizerStyles);
    const count = this.cell?.table?.props.columnWidths.length ?? 0;
    if (count > 0 && this.cell?.columnIndex === count - 1) {
      this.dataset.last = 'true';
    }
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerup', this._onPointerUp);
    this.addEventListener('pointercancel', this._onPointerUp);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerup', this._onPointerUp);
    this.removeEventListener('pointercancel', this._onPointerUp);
  }

  override render() {
    return html`<div class="${richTableResizerLineStyles}"></div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-rich-table-resizer': RichTableResizerComponent;
  }
}
